"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { NotificationCourse } from "../types/models/notification-course";
import { showToast } from "../components/shared/feedback/custom-toast-shortcut";
import { useKmsService } from "../providers/kms-service-provider";
import { useUser } from "../providers/user-provider";
import { roleUserVerification } from "../utils/role-user-verification";
import { CAN_ACCESS_COURSES_ADMIN } from "../constants/permissions-const";
import { CourseStatut } from "../types/records/course-statut-record";
import {
  detailNotificationCourse,
  titreNotificationCourse,
} from "../utils/notification-course-labels";

/**
 * Le fil de notifications de la cloche, et les toasts qui l'accompagnent.
 *
 * Aucun filtrage par rôle sur ce qui arrive du hub : le serveur ne diffuse ces
 * évènements qu'au groupe `gestionnaires`, un client connecté n'en reçoit tout
 * simplement pas. Le rôle n'est consulté que pour décider s'il faut aller
 * chercher le fil en HTTP — inutile d'appeler une route qui répondrait 401.
 */

/** Ce que la cloche garde en mémoire. Au-delà, le fil est en base, pas à l'écran. */
const FIL_MAX = 20;

export const useNotificationsCourses = (
  ouvrirCourse?: (idCourse: number) => void,
) => {
  const { me } = useUser();
  const { notificationService } = useKmsService();
  const t = useTranslations("conversation");
  const tStatut = useTranslations("course_statut");

  const notificationLabels = {
    newRide: (id: number) => t("notification_new_ride", { id }),
    rideModified: (id: number) => t("notification_ride_modified", { id }),
    rideAssigned: (id: number) => t("notification_ride_assigned", { id }),
    rideStatus: (id: number, statut: string) =>
      t("notification_ride_status", { id, status: statut }),
    priceLine: (from: string, to: string) =>
      t("notification_price_line", { from, to }),
    driverFallback: t("notification_driver_fallback"),
    statusChanged: tStatut("status_changed"),
    statutLabels: {
      [CourseStatut.PaymentAttente]: tStatut("payment_pending"),
      [CourseStatut.Dispatching]: tStatut("dispatching"),
      [CourseStatut.PreReserve]: tStatut("pre_reserved"),
      [CourseStatut.Affecte]: tStatut("assigned"),
      [CourseStatut.Annulee]: tStatut("cancelled"),
      [CourseStatut.ClientAbsent]: tStatut("client_absent"),
      [CourseStatut.FausseCourse]: tStatut("fake_ride"),
      [CourseStatut.Terminee]: tStatut("completed"),
      [CourseStatut.EnCours]: tStatut("in_progress"),
      [CourseStatut.ChauffeurEnRoute]: tStatut("driver_en_route"),
      [CourseStatut.ChauffeurSurPlace]: tStatut("driver_on_site"),
    },
  };

  const [notifications, setNotifications] = useState<NotificationCourse[]>([]);
  const [nombreNonVues, setNombreNonVues] = useState(0);

  const estBackOffice = !!roleUserVerification(me?.role, [
    CAN_ACCESS_COURSES_ADMIN,
  ]);

  const ouvrirCourseRef = useRef(ouvrirCourse);
  ouvrirCourseRef.current = ouvrirCourse;

  /**
   * Recharge le fil depuis le serveur. Appelé au montage et à chaque
   * rétablissement du canal : ce qui a été diffusé pendant une coupure n'a
   * atteint personne, et seule la base en garde trace.
   */
  const rafraichir = useCallback(async () => {
    if (!estBackOffice) return;

    try {
      const fil = await notificationService.getFil(FIL_MAX);
      setNotifications(fil.notifications);
      setNombreNonVues(fil.nombreNonVues);
    } catch {
      // Une cloche muette vaut mieux qu'une page cassée : le suivi des courses
      // n'est pas ce qui doit faire tomber le back-office.
    }
  }, [estBackOffice, notificationService]);

  useEffect(() => {
    void rafraichir();
  }, [rafraichir]);

  /** Un évènement poussé par le hub : il entre dans le fil et passe en toast. */
  const onNotificationCourse = useCallback((notification: NotificationCourse) => {
    setNotifications((precedentes) =>
      // Le rechargement d'après-coupure peut ramener une notification déjà
      // reçue en direct : l'identifiant tranche.
      precedentes.some((existante) => existante.id === notification.id)
        ? precedentes
        : [notification, ...precedentes].slice(0, FIL_MAX),
    );
    setNombreNonVues((precedent) => precedent + 1);

    showToast.course(
      titreNotificationCourse(notification, notificationLabels),
      detailNotificationCourse(notification, notificationLabels),
      ouvrirCourseRef.current &&
        (() => ouvrirCourseRef.current?.(notification.idCourse)),
    );
  }, [notificationLabels]);

  /**
   * Marque le fil comme vu jusqu'à la notification la plus récente affichée.
   * Le compteur retombe sans attendre le serveur : c'est un geste de lecture,
   * pas une opération dont l'utilisateur attend le résultat.
   */
  const marquerVues = useCallback(async () => {
    const dernier = notifications[0]?.id;

    if (!dernier || nombreNonVues === 0) return;

    setNombreNonVues(0);

    try {
      await notificationService.marquerVues(dernier);
    } catch {
      // Le marque-page n'a pas avancé : le prochain chargement recomptera ces
      // notifications comme non vues, ce qui est le bon repli.
    }
  }, [notifications, nombreNonVues, notificationService]);

  return {
    notifications,
    nombreNonVues,
    /** À brancher sur le hub temps réel. */
    onNotificationCourse,
    /** À appeler au rétablissement du canal. */
    rafraichir,
    marquerVues,
  };
};
